'use client';

import { PropsWithChildren, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useShell } from '@/providers';
import { useIsRegistryRoute } from '@/hooks/useIsRegistryRoute';
import { Box, Stack } from '@/primitives';
import DesktopToolbar from './toolbar/DesktopToolbar';
import MobileToolbar from './toolbar/MobileToolbar';
import ShellSidebar from './ShellSidebar';
import ShellFooter from './ShellFooter';
import { MainContentErrorBoundary } from '@/components/ErrorBoundary/MainContentErrorBoundary';
import GraphQLProvider from '@/providers/GraphQLProvider';
import {
  FOOTER_HEIGHT,
  MOBILE_FOOTER_HEIGHT,
  NAV_HEIGHT,
  PAGE_PADDING,
  PAGE_PADDING_MOBILE,
  PAGE_PADDING_TOP,
} from '@/configs/layout';

export function Shell({ children }: PropsWithChildren) {
  const pathname = usePathname();
  const router = useRouter();
  const { isMobile } = useShell();
  const isRegistryRoute = useIsRegistryRoute();
  const isFixedFooterPage =
    pathname === '/' ||
    pathname === '/registry' ||
    pathname === '/terms' ||
    pathname === '/privacy';

  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) router.refresh();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [router]);

  return (
    <GraphQLProvider>
      <Stack
        id="shell"
        sx={{
          flex: 1,
          mt: `${NAV_HEIGHT}px`,
          ...(!isRegistryRoute && {
            // Ensure shell fills viewport on home; reserve space for fixed footer only there
            minHeight: `calc(100vh - ${NAV_HEIGHT}px)`,
            height: {
              // prevent double scroll bar on android/ios devices
              md: `calc(100vh - ${NAV_HEIGHT}px)`,
            },
            overflowY: 'auto',
            overflowX: 'hidden',
            ...(isFixedFooterPage && {
              pb: { xs: MOBILE_FOOTER_HEIGHT, md: FOOTER_HEIGHT },
            }),
          }),
        }}
      >
        {isMobile ? <MobileToolbar /> : <DesktopToolbar />}
        {isRegistryRoute ? (
          <Stack
            direction="row"
            height={`calc(100vh - ${NAV_HEIGHT}px - ${FOOTER_HEIGHT}px)`}
          >
            <ShellSidebar />
            <Box
              component="main"
              id="main"
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                pt: `${PAGE_PADDING_TOP}px`,
                px: {
                  xs: `${PAGE_PADDING_MOBILE}px`,
                  sm: `${PAGE_PADDING}px`,
                },
                pb: 0,
                height: `calc(100vh - ${NAV_HEIGHT}px - ${FOOTER_HEIGHT}px)`,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                <MainContentErrorBoundary>{children}</MainContentErrorBoundary>
              </Box>
            </Box>
          </Stack>
        ) : (
          <Stack
            component="main"
            flex={1}
            sx={{ minHeight: 0, display: 'flex' }}
          >
            <MainContentErrorBoundary>{children}</MainContentErrorBoundary>
          </Stack>
        )}
        <ShellFooter fixed={isFixedFooterPage} />
      </Stack>
    </GraphQLProvider>
  );
}
